/* ------------------- IMPORTS */
import styled from '@emotion/styled'
import { Box, Icons } from 'components'
import { openEmojidModal } from 'components/Modal/ModalManager'
import { useEffect, useRef, useState } from 'react'
import { useCreatePostStore } from 'stores/CreatePostStore'
import { AppStore, T } from 'utils'
import { extractAndCompareUrls, getActiveDescription, getActiveText, updateActive } from 'utils/PostUtils/Post'
import {
  canShowMetadata,
  getLinkProperties,
  manageGlobalLinkUpdate,
  manageVariantLinkUpdate,
  onPasteLink,
} from 'utils/PostUtils/ShortUrl'
import { Metadata } from './Metadata/Metadata'
import { TextPostInfo } from './TextInfo/TextPostInfo'
import { Utm } from './Utm/Utm'

/* ------------------- INTERFACES  */
interface Props {}

export const TextContainer = ({}: Props) => {
  /* ----------------- ZUSTAND  */
  const { activePost, posts, textLoading } = useCreatePostStore((s) => ({
    activePost: s.activePost as any,
    posts: s.posts,
    textLoading: s.textLoading,
  }))

  /* ----------------- REF  */
  const inputRef = useRef<any>(null)

  const [forceUpdate, setForceUpdate] = useState(false)

  /* ----------------- VARIBLES  */
  const showMetadata = canShowMetadata()

  const showUtm = getLinkProperties()!.length > 0

  const isYoutube = activePost && activePost?.info?.account?.provider === 'youtube'

  /* ----------------- METHODS  */
  // Calcola l'altezza del Textarea in base alle condizioni date
  const calculateTextareaHeight = () => {
    if (isYoutube) {
      return '55px'
    } else {
      let height = 604 // su figma le preview hanno altezze diverse, questa è messa perchè sembra la più bilanciata a livello di padding

      if (showUtm && utmProviderCheck()) {
        height -= 56
      }

      if (showMetadata && utmProviderCheck()) {
        height -= 160
      }

      return `${height}px`
    }
  }

  const calculateTextareaHeightDescriptionYT = () => {
    let height = 545

    if (showUtm && utmProviderCheck()) {
      height -= 56
    }

    return `${height}px`
  }

  const utmProviderCheck = () => {
    const provider = activePost && activePost?.info?.account?.provider
    const bannedProviders = ['instagram', 'tiktok']
    return !bannedProviders.includes(provider)
  }

  const linkCheck = () => {
    return activePost?.data?.urls?.length > 0 || activePost?.urls?.length > 0
  }

  const onTextAreaChange = async (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setForceUpdate(!forceUpdate)
    updateActive({ text: e.target.value })

    // // Update the urls in the active post
    // const newUrls = extractAndCompareUrls(
    //   activePost?.text ?? activePost?.data?.text,
    //   activePost?.urls ?? activePost?.data?.urls ?? []
    // )

    // // update urls in the active post
    // updateActive({ urls: newUrls })
    //removeDuplicatedUrls()
  }

  const removeDuplicatedUrls = () => {
    // check urls of the active post and remove the duplicated ones
    const urls = activePost?.data?.urls ?? activePost?.urls ?? []

    const uniqueUrls = urls.filter((url, index) => {
      return urls.findIndex((u) => u.url === url.url) === index
    })

    updateActive({ urls: uniqueUrls })
  }

  const verifyUrls = async (activeText: string, description: string): Promise<void> => {
    // extract urls from the text wit all the parameters
    useCreatePostStore.setState({ textLoading: true })

    const urlRegex =
      /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/g

    // Urls in the active text
    const contentUrls: string[] = description?.match(urlRegex) ?? []
    // console.log('contentUrls', contentUrls)
    // check if urls are already present in the active post
    // const urls = activePost?.data?.urls ?? []
    // const urlsInPost = urls.map((url) => url.url)
    // console.log('urlsInPost', urlsInPost)
    // Check if users has social accounts with ref parameters in his workspace
    // if (!AppStore.shortenAvailable) {
    //   console.log('AppStore.shortenAvailable', AppStore.shortenAvailable)
    //   const { socialsAdded, globalPost } = useCreatePostStore.getState()

    //   // If this parameter is present user is modifying a single post
    //   if (activePost?.data) {
    //     await manageVariantLinkUpdate(contentUrls, activePost, urlRegex, description)
    //     return
    //   } else if (socialsAdded.length > 0) {
    //     //Global post with social accounts added
    //     const updatedPosts = await manageGlobalLinkUpdate(contentUrls, posts, urlRegex)
    //     // Update the posts in the store
    //     useCreatePostStore.setState({ posts: updatedPosts })
    //     return
    //   }

    //   // If user is editing a global post and has no social accounts added in section return and wait some user action
    //   return
    // } else {

    const updatedPosts = await manageGlobalLinkUpdate(contentUrls, posts, urlRegex)
    // Update the posts in the store
    useCreatePostStore.setState({ posts: updatedPosts })

    onPasteLink({ activePost, newText: description, finalText: activeText })

    // if user has no ref parameters in his workspace then do the classic shortening logic
    // }

    useCreatePostStore.setState({ textLoading: false })
  }

  /* ----------------- USEEFFECT  */
  useEffect(() => {
    //   // console.log('activePost?.info', activePost?.info)
    verifyUrls(getActiveText(), getActiveText())
  }, [posts.length, activePost?.info])

  const handlePasteText = async (event: any) => {
    event.preventDefault()
    const pastedText = event.clipboardData.getData('text')
    const start = inputRef?.current?.selectionStart
    const end = inputRef?.current?.selectionEnd

    const inputValue = getActiveText()
    const beforePaste = inputValue?.substring(0, start)
    const afterPaste = inputValue?.substring(end)

    const processedText = pastedText
    const newValue = beforePaste + processedText + afterPaste

    updateActive({ text: newValue })

    await verifyUrls(newValue, pastedText)
  }

  return (
    <TextContainerContainer>
      {/* Textarea  generale */}
      <TextareaContainer
        style={{
          height: calculateTextareaHeight(),
        }}
      >
        {/* Titolo del post */}
        <TextAreaPost
          style={{ opacity: !textLoading ? 1 : 0.5 }}
          disabled={textLoading}
          ref={inputRef}
          id="text-area-create-post"
          value={getActiveText()}
          onChange={onTextAreaChange}
          onPaste={handlePasteText}
          placeholder={isYoutube ? T.postPage.insertHereTextYouTube : T.postPage.insertHereText}
        />

        {!isYoutube && <EmojiIcon onClick={() => openEmojidModal('text-area-create-post')} />}

        {/* Commentato "Ricevi da globale" */}
        {/* {activePost?._id !== 'GLOBAL' && isUnique(activePost?.variantId) && <GlobalPull />} */}
      </TextareaContainer>

      {/* Textarea  descrizione youtube */}
      {isYoutube && (
        <TextareaContainer>
          <TextAreaPost
            id="text-area-create-post-description"
            style={{ height: calculateTextareaHeightDescriptionYT() }}
            value={activePost.data.description}
            placeholder={T.postPage.changeDescriptionYouTube}
            onChange={(e) => {
              const description = e.target.value
              const activeDescription = getActiveDescription() ?? ''
              if (description.length > activeDescription.length + 1)
                onPasteLink({ activePost, newText: description, isDescription: true })
              updateActive({ description })
            }}
          />
          <EmojiIcon onClick={() => openEmojidModal('text-area-create-post-description')} />
        </TextareaContainer>
      )}

      {/* Metadata */}
      {linkCheck() && showMetadata && utmProviderCheck() && <Metadata post={activePost} />}

      {/* UTM */}
      {linkCheck() && showUtm && utmProviderCheck() && <Utm post={activePost} isDescription={isYoutube} />}

      {/* Length text check, time to read, Text difficulty */}
      <TextPostInfo />
    </TextContainerContainer>
  )
}

const TextContainerContainer = styled(Box)`
  display: flex;
  justify-content: space-between;
  height: 100%;
  gap: 16px;
`

const TextareaContainer = styled(Box)`
  border: 1px solid ${() => AppStore.theme.o.grey};
  position: relative;
  border: none;
  width: 442px;
`

const EmojiIcon = styled((props) => <Icons.emojiIcons {...props} />)`
  width: 35px;
  height: 35px;
  position: absolute;
  bottom: 10px;
  right: 10px;
  transition: 300ms ease-in-out;
  cursor: pointer;
  fill: ${AppStore.theme.o.darkGrey};

  :hover {
    fill: ${AppStore.theme.o.grey};
  }
`

const TextTitlePost = styled.input`
  width: 100%;
  height: 100%;
  padding: 16px;
  border-radius: 16px;
  resize: none;
  background: ${() => AppStore.theme.o.lightestGrey};
  transition: 300ms ease-in-out;
  color: ${() => AppStore.theme.o.black};
  font-weight: 600;
  border: 1px solid ${() => AppStore.theme.o.grey};
  box-sizing: border-box;

  &:hover,
  &:focus {
    background: ${() => AppStore.theme.o.lightGrey};
  }

  &::placeholder {
    font-weight: 600;
    color: ${() => AppStore.theme.o.darkGrey};
  }
`

const TextAreaPost = styled.textarea`
  width: 100%;
  height: 100%;
  padding: 16px;
  border-radius: 16px;
  scrollbar-width: none;
  resize: none;
  border: 1px solid ${() => AppStore.theme.o.grey};
  background: ${() => AppStore.theme.o.lightestGrey};
  transition: 300ms ease-in-out;
  color: ${() => AppStore.theme.o.black};
  font-weight: 600;

  &:hover,
  &:focus {
    background: ${() => AppStore.theme.o.lightGrey};
  }

  &::placeholder {
    font-weight: 600;
    color: ${() => AppStore.theme.o.darkGrey};
  }
`
